/* assets/gcitypartners-provider-portal-native.js — GCITYPARTNERS-PROVIDER-PORTAL-NATIVE-05
 * Presentation consumer of the canonical LittleGWebSession.
 * Never grants Provider access. Backend provider_approvals remains the authorization authority.
 * Provider Portal consumes an existing GCityPartners session; canonical sign-in lives at /pages/gcitypartners-sign-in.
 */
(function (window, document) {
  'use strict';

  var ROOT = '[data-provider-portal]';
  var ACCESS_URL = 'https://api.glhksolution.com/v1/gcitypartners/provider/rfqs?limit=1';
  var CONTEXT_BAR = '[data-module="site-header"][data-context-bar]';
  /**
   * @typedef {{
   *   status?: string,
   *   profile?: {id?: unknown} | null
   * }} ProviderSessionState
   */
  /**
   * @typedef {Readonly<{
   *   getState: () => ProviderSessionState | null,
   *   authenticatedFetch: (url: string, options?: RequestInit) => Promise<Response>,
   *   ready: () => Promise<unknown>,
   *   logout: () => Promise<unknown>
   * }>} ProviderSessionConsumer
   */
  /**
   * @typedef {Readonly<{
   *   mountAll: (scope?: ParentNode | null) => void,
   *   refresh: () => void
   * }>} ProviderPortalPresentation
   */
  /** @type {WeakSet<Element>} */
  const mounted = new WeakSet();
  /** @type {WeakMap<Element, number>} */
  const resolveVersions = new WeakMap();
  /**
   * @type {Window & {
   *   GCityPartnersProviderPortalNative?: ProviderPortalPresentation,
   *   LittleGWebSession?: ProviderSessionConsumer | null
   * }}
   */
  const portalWindow = window;
  const existingPortal = portalWindow.GCityPartnersProviderPortalNative;

  if (existingPortal && typeof existingPortal.mountAll === 'function') {
    existingPortal.mountAll(document);
    return;
  }

  function clean(value) {
    return String(value == null ? '' : value).trim();
  }
  function session() {
    const owner = portalWindow.LittleGWebSession;
    return owner &&
      typeof owner.getState === 'function' &&
      typeof owner.authenticatedFetch === 'function'
      ? owner
      : null;
  }
  /**
   * @param {Element} root
   * @param {'checking' | 'guest' | 'error' | 'allowed' | 'denied'} value
   */
  function state(root, value) {
    root.setAttribute('data-native-provider-state', value);
    root.setAttribute(
      'data-portal-authenticated',
      value === 'allowed' || value === 'checking' || value === 'denied' ? 'true' : 'false'
    );
  }
  /** @param {Element | null} root */
  function syncProviderShell(root) {
    if (!root || !root.isConnected) return;
    var bar = document.querySelector(CONTEXT_BAR);
    if (!bar) return;

    var title = bar.querySelector('[data-context-title]');
    if (title) {
      title.textContent = 'GCityPartners';
      title.setAttribute('data-provider-context-title', 'true');
    }

    var shopifyAccount = bar.querySelector(
      '[data-context-account-shortcut][data-identity-authority="shopify-customer-accounts"]'
    );
    if (shopifyAccount instanceof HTMLElement) {
      shopifyAccount.hidden = true;
      shopifyAccount.setAttribute('aria-hidden', 'true');
      shopifyAccount.setAttribute('data-provider-portal-suppressed', 'true');
    }
  }
  function syncProviderShellFromDocument() {
    syncProviderShell(document.querySelector(ROOT));
  }
  /** @param {Element} root */
  async function resolve(root) {
    const version = (resolveVersions.get(root) || 0) + 1;
    resolveVersions.set(root, version);
    syncProviderShell(root);
    state(root, 'checking');

    const owner = session();
    if (!owner) {
      state(root, 'guest');
      return;
    }

    function latest() {
      return root.isConnected && resolveVersions.get(root) === version && session() === owner;
    }

    if (typeof owner.ready === 'function') {
      try {
        await owner.ready();
      } catch (_error) {
        if (latest()) state(root, 'error');
        return;
      }
    }
    if (!latest()) return;

    const current = owner.getState();
    const principalId = clean(current && current.profile && current.profile.id);
    if (!current || current.status !== 'authenticated' || !principalId) {
      state(root, 'guest');
      return;
    }

    function samePrincipal() {
      if (!owner || !latest()) return false;
      const active = owner.getState();
      return (
        active &&
        active.status === 'authenticated' &&
        clean(active.profile && active.profile.id) === principalId
      );
    }

    try {
      const response = await owner.authenticatedFetch(ACCESS_URL, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
      const payload = await response.json().catch(function () {
        return {};
      });

      if (!latest()) return;
      if (!samePrincipal()) {
        void resolve(root);
        return;
      }

      if (response.ok && payload && payload.ok === true) {
        state(root, 'allowed');
      } else if (
        response.status === 403 &&
        clean(payload && payload.error) === 'provider_not_approved'
      ) {
        state(root, 'denied');
      } else if (response.status === 401) {
        state(root, 'guest');
      } else {
        state(root, 'error');
      }
    } catch (_error) {
      if (!latest()) return;
      if (!samePrincipal()) {
        void resolve(root);
        return;
      }
      state(root, 'error');
    }
  }
  /** @param {Element | null} root */
  function mount(root) {
    if (!root || mounted.has(root)) return;
    mounted.add(root);
    syncProviderShell(root);
    var logout = root.querySelector('[data-provider-native-logout]');
    if (logout)
      logout.addEventListener('click', function () {
        resolveVersions.set(root, (resolveVersions.get(root) || 0) + 1);
        state(root, 'guest');

        const owner = session();
        if (owner && typeof owner.logout === 'function') {
          Promise.resolve()
            .then(function () {
              return owner.logout();
            })
            .catch(function () {
              if (!root.isConnected) return;
              state(root, 'error');
            });
        }
      });
    void resolve(root);
  }
  function mountAll(scope) {
    var base = scope && scope.querySelectorAll ? scope : document;
    if (base.matches && base.matches(ROOT)) mount(base);
    Array.prototype.forEach.call(base.querySelectorAll(ROOT), mount);
    syncProviderShellFromDocument();
  }
  function refresh() {
    syncProviderShellFromDocument();
    Array.prototype.forEach.call(document.querySelectorAll(ROOT), function (root) {
      void resolve(root);
    });
  }

  window.GCityPartnersProviderPortalNative = Object.freeze({
    mountAll: mountAll,
    refresh: refresh,
  });
  document.addEventListener('littleg:web-session-change', refresh);
  document.addEventListener('shopify:section:load', function (event) {
    mountAll(event.target instanceof Element ? event.target : document);
    syncProviderShellFromDocument();
  });
  window.addEventListener('pageshow', syncProviderShellFromDocument);
  if (document.readyState === 'loading')
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        mountAll(document);
      },
      { once: true }
    );
  else mountAll(document);
})(window, document);
